import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@resources/css/leaflet.rrose.css';
import '@resources/css/theme/imo/leaflet-map.css';
import '@resources/js/app/composables/leaflet/leaflet.rrose-src.js';
import '@resources/js/app/composables/leaflet/leaflet.patches.js';
import pin from '@resources/svg/marker-secondary-fill.svg';

let self = null;
export default $wire => ({
    map: null,
    markersLayer: null,
    markersSelected: [],
    markersIds: {},
    pinsToAdd: null,

    zoom: 15,
    squareAreaRadius: 0.00676,
    tabId: $wire.tabId,
    mapUrl: $wire.mapUrl,
    mapTilesSubdomains: $wire.mapTilesSubdomains,
    mapIndex: $wire.entangle('index').live,
    center: $wire.entangle('center').live,
    hideExactLocation: $wire.entangle('hideExactLocation').live,
    mapTabs: $wire.entangle('mapTabs').live,
    locations: $wire.entangle('locations').live,
    similarListings: $wire.entangle('similarListings').live,
    responses: [],

    init() {
        $wire.load();
        self = this;
    },

    tabChanged($event) {
        self.tabId = $event.detail;
        if (self.mapTabs.includes($event.detail)) {
            if (!self.map) {
                self.createMap();
            }
            self.displayPinsOnMap();
        }
    },

    mapSizeChanged() {
        if (self.map) {
            setTimeout(
                map => {
                    map.invalidateSize();
                },
                400,
                self.map,
            );
        }
    },

    displayPinsOnMap() {
        if (self.tabId !== 'location') {
            self.getPOIPinsToShowOnMap().then(response => {
                let newpins = [];

                if (typeof response != 'undefined' && typeof response.data != 'undefined') {
                    newpins = response.data.features || [];
                }

                self.getPinsToDisplay(newpins);
                self.deletePins();

                if (self.pinsToAdd) {
                    self.createPOIPins();
                }
            });
        } else {
            self.deletePins();
            self.displaySimilarListingsPins();
            if (self.pinsToAdd) {
                self.map.off('moveend');
            }
            self.map.on('moveend', self.displaySimilarListingsPins);
            $wire.logRAOSimilarListings();
        }
    },

    async getPOIPinsToShowOnMap() {
        return new Promise(async resolve => {
            if (self.map) {
                if (self.responses[self.tabId]) {
                    resolve(self.responses[self.tabId]);
                } else {
                    try {
                        let response = window.axios.post('/api/v1/points-of-interest', {
                            locations: self.locations,
                            category: self.tabId,
                            center: self.center,
                        });

                        self.responses[self.tabId] = response;
                        resolve(response);
                    } catch (e) {}
                }
            }

            resolve({});
        });
    },

    getPinsToDisplay(newPins) {
        self.pinsToAdd = newPins;
    },

    deletePins() {
        self.markersLayer.clearLayers();
    },

    createPOIPins() {
        self.markersSelected = [];

        for (let key in self.pinsToAdd) {
            let icon = self.createIcons(self.pinsToAdd[key]);
            let center = [
                self.pinsToAdd[key].geometry?.coordinates[1] ?? 0,
                self.pinsToAdd[key].geometry?.coordinates[0] ?? 0,
            ];
            let tooltipText =
                self.pinsToAdd[key].properties?.name +
                '<br/>(aprox. ' +
                self.pinsToAdd[key].properties?.display_distance +
                ')';

            let marker = new L.Marker(center, {
                icon: icon,
            });

            self.markersIds[key] = self.markersLayer.getLayerId(marker);
            self.markersSelected.push(marker);

            marker.on('mouseover', function (e) {
                this.bindTooltip(tooltipText, {
                    direction: 'top',
                    offset: L.point({ x: 0, y: -12 }),
                }).openTooltip();
            });
            marker.on('mouseout', function (e) {
                this.unbindTooltip();
            });
        }

        self.map.off('moveend');
        self.map.on('moveend', self.displayPOIPins);
        self.displayPOIPins();
    },

    displayPOIPins() {
        self.map.removeLayer(self.markersLayer);
        self.deletePins();

        const screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        const bounds = self.map.getBounds();
        const markers = self.markersSelected;
        const viewLimit = screenWidth < 1200 ? 100 : 300;

        let i = 0;
        for (let j = 0; j < markers.length && i < viewLimit; j++) {
            const marker = markers[j];
            if (bounds.contains(marker.getLatLng())) {
                self.markersLayer.addLayer(marker);
                i++;
            }
        }

        self.map.addLayer(self.markersLayer);
    },

    displaySimilarListingsPins() {
        self.map.removeLayer(self.markersLayer);

        for (let key in self.similarListings) {
            let icon = new L.divIcon({
                className: 'search-results-map-listing-icon',
                html: self.similarListings[key].price_map || '',
                iconSize: [self.similarListings[key].price_map.length * 10, 20],
            });

            let center = [
                self.similarListings[key].geo_location?.lat ?? 0,
                self.similarListings[key].geo_location?.lon ?? 0,
            ];

            let marker = new L.Marker(center, {
                icon: icon,
            });

            marker
                .bindPopup(self.getPopupContainerTemplate(self.similarListings[key]), {
                    className: 'similar_listings__main-container',
                    offset: new L.point({ x: 25, y: 15 }),
                    opacity: 0.8,
                })
                .on('click', () => {
                    if (!marker.isPopupOpen()) {
                        marker.openPopup();
                    }
                });

            self.markersLayer.addLayer(marker);
            self.markersIds['similar_listings_' + key] = self.markersLayer.getLayerId(marker);
        }

        self.map.addLayer(self.markersLayer);
    },

    getPopupContainerTemplate(listing) {
        let id = listing.id;
        let image = listing.image;
        let price = listing.price;
        let location = listing.location;
        let locationType = listing.location_type;
        let url = listing.url;
        let details = listing.details;

        let popupContainer = document.createElement('div');
        popupContainer.classList.add('similar_listings__container');
        popupContainer.innerHTML += `<div class="similar_listing__wrapper__header">
                <a
                    href="${url}"
                    target="_blank"
                    x-on:click.prevent="openRecommendedListing('${id}', '${listing.url}')"
                >
                    <div class="similar-listing__card">
                        <div class="similar-listing__img-wrapper__header">
                            <img class="similar-listing__img" src="${image}">
                        </div>
                        <div class="similar-listing__details-wrapper__header">
                            <span class="similar-listing__price"></span>
                            <span class="similar-listing__location"></span>
                            <span class="similar-listing__details"></span>
                            <span class="similar-listing__location_type"></span>
                        </div>
                        <span class="similar-listing__right">></span>
                    </div>
                </a>
            </div>`;

        popupContainer.querySelector('.similar-listing__price').innerHTML = price + '</br>';
        popupContainer.querySelector('.similar-listing__location').innerHTML += location + '</br>';
        popupContainer.querySelector('.similar-listing__details').innerHTML += details + '</br>';
        popupContainer.querySelector('.similar-listing__location_type').innerHTML += locationType + '</br>';

        return popupContainer;
    },

    async createMap() {
        let mapId = `map-gallery`;

        self.map = L.map(mapId, {
            boxZoom: false,
            debounceMoveend: true,
            doubleClickZoom: false,
            fadeAnimation: false,
            markerZoomAnimation: false,
            maxZoom: 18,
            minZoom: 9,
            zoomAnimation: false,
            zoomControl: true,
        }).on('load', async () => {
            setTimeout(function () {
                self.map.invalidateSize(true);
            }, 100);
        });

        self.map.setView(self.center, self.zoom);

        new L.tileLayer(self.mapUrl, {
            attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
            opacity: 0.7,
            subdomains: self.mapTilesSubdomains,
            zIndex: 30,
        }).addTo(self.map);

        const iconWidth = window.innerWidth < 768 ? 28 : 60;
        const iconHeight = window.innerWidth < 768 ? 32 : 72;
        const icon = L.icon({
            iconUrl: pin,
            iconSize: [iconWidth, iconHeight],
            iconAnchor: [iconWidth / 2, iconHeight + 1],
        });

        if (self.hideExactLocation) {
            const rectBounds = [
                [self.center[0] - self.squareAreaRadius / 2, self.center[1] - self.squareAreaRadius / 2],
                [self.center[0] + self.squareAreaRadius / 2, self.center[1] + self.squareAreaRadius],
            ];
            L.rectangle(rectBounds, {
                color: '#048BA8',
                weight: 1,
            }).addTo(self.map);
        } else {
            new L.marker(self.center, {
                icon,
            }).addTo(self.map);
        }

        self.markersLayer = L.layerGroup();

        L.Tooltip.prototype._updatePosition = function () {
            if (!this._map) {
                return;
            }
            var pos = this._map.latLngToLayerPoint(this._latlng);
            this._setPosition(pos);
        };
    },

    createIcons(feature) {
        return L.icon({
            iconUrl: feature.properties?.icon ?? pin,
            iconSize: [22, 22],
            iconAnchor: [11, 11],
        });
    },

    openRecommendedListing(listingId, path) {
        $wire.openRecommendedListing(listingId, path);
    },
});
